// Phase 9B — TrustCenterPage.
//
// Knowledge Base + Trust Center shell. Shows trust transparency
// metrics, methodology docs, trust policies, FAQs, and system
// documentation in a premium searchable layout.

import { useState, useMemo } from "react";
import {
  useKbArticles,
  useTrustMetrics,
} from "../../knowledge/useKnowledgeBase.ts";
import type {
  KbArticleType,
  KbArticle,
  TrustMetric,
} from "../../knowledge/useKnowledgeBase.ts";
import * as s from "./trust.styles.ts";

// ─── Constants ────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  methodology: "Methodology",
  trust_policy: "Trust Policy",
  faq: "FAQ",
  glossary: "Glossary",
  tutorial: "Tutorial",
  release_note: "Release",
  system_doc: "System",
};

const ARTICLE_TABS: { key: "all" | KbArticleType; label: string }[] = [
  { key: "all", label: "All" },
  { key: "methodology", label: "Methodology" },
  { key: "trust_policy", label: "Policies" },
  { key: "faq", label: "FAQ" },
  { key: "glossary", label: "Glossary" },
  { key: "tutorial", label: "Tutorials" },
  { key: "release_note", label: "Releases" },
  { key: "system_doc", label: "System" },
];

// ─── Metric Card ──────────────────────────────────────────────

function MetricCard({ metric }: { metric: TrustMetric }): JSX.Element {
  const displayValue = metric.value_text ??
    (metric.value_num !== null ? String(metric.value_num) : "—");

  return (
    <div style={s.metricCard}>
      <span style={s.metricValue}>{displayValue}</span>
      <span style={s.metricLabel}>{metric.metric_label}</span>
      {metric.description ? (
        <span style={s.metricDesc}>{metric.description}</span>
      ) : null}
    </div>
  );
}

// ─── Article Card ─────────────────────────────────────────────

function ArticleCard({
  article,
  onSelect,
}: {
  article: KbArticle;
  onSelect: (a: KbArticle) => void;
}): JSX.Element {
  const typeStyle = s.TYPE_STYLES[article.article_type] ?? s.TYPE_STYLES.system_doc;

  return (
    <div
      style={article.pinned ? s.articleCardPinned : s.articleCard}
      onClick={() => onSelect(article)}
      role="button"
      tabIndex={0}
    >
      <div style={s.articleMeta}>
        <span style={typeStyle}>
          {TYPE_LABELS[article.article_type] ?? article.article_type}
        </span>
        <span style={s.articleCategory}>{article.category}</span>
      </div>
      <span style={s.articleTitle}>{article.title}</span>
      {article.summary ? (
        <span style={s.articleSummary}>{article.summary}</span>
      ) : null}
      {article.tags.length > 0 ? (
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
          {article.tags.slice(0, 4).map((t) => (
            <span key={t} style={s.articleTag}>
              {t}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── Article Detail ───────────────────────────────────────────

function ArticleDetail({
  article,
  onBack,
}: {
  article: KbArticle;
  onBack: () => void;
}): JSX.Element {
  const typeStyle = s.TYPE_STYLES[article.article_type] ?? s.TYPE_STYLES.system_doc;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sz-space-3)" }}>
      <button type="button" style={s.backButton} onClick={onBack}>
        ← Back to articles
      </button>
      <div style={s.articleDetail}>
        <div style={s.articleMeta}>
          <span style={typeStyle}>
            {TYPE_LABELS[article.article_type] ?? article.article_type}
          </span>
          <span style={s.articleCategory}>{article.category}</span>
        </div>
        <span style={{ ...s.articleTitle, fontSize: "1.4rem" }}>
          {article.title}
        </span>
        {article.summary ? (
          <span style={{ ...s.articleSummary, fontSize: "var(--sz-body-md)" }}>
            {article.summary}
          </span>
        ) : null}
        <div
          style={{
            height: "1px",
            background: "var(--sz-border-subtle)",
          }}
        />
        <div style={s.articleContent}>{article.content}</div>
        {article.tags.length > 0 ? (
          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {article.tags.map((t) => (
              <span key={t} style={s.articleTag}>
                {t}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export function TrustCenterPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<"all" | KbArticleType>("all");
  const [selectedArticle, setSelectedArticle] = useState<KbArticle | null>(
    null,
  );

  const articlesState = useKbArticles(
    activeTab === "all" ? undefined : activeTab,
  );
  const metricsState = useTrustMetrics();

  // Group metrics by category
  const metricsByCategory = useMemo(() => {
    const groups = new Map<string, TrustMetric[]>();
    for (const m of metricsState.metrics) {
      const existing = groups.get(m.category) ?? [];
      existing.push(m);
      groups.set(m.category, existing);
    }
    return groups;
  }, [metricsState.metrics]);

  // Type counts for tab badges
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of articlesState.articles) {
      counts[a.article_type] = (counts[a.article_type] ?? 0) + 1;
    }
    return counts;
  }, [articlesState.articles]);

  return (
    <div style={s.trustRoot}>
      {/* Header */}
      <div style={s.trustHeader}>
        <div>
          <h2 style={s.trustTitle}>Trust Center</h2>
          <div style={s.trustSubtitle}>
            Transparency metrics · methodology docs · trust policies
          </div>
        </div>
        <span style={s.sectionCount}>
          {String(metricsState.metrics.length)} metrics ·{" "}
          {String(articlesState.articles.length)} articles
        </span>
      </div>

      {/* Trust Metrics */}
      {metricsState.metrics.length > 0 ? (
        <>
          {Array.from(metricsByCategory.entries()).map(
            ([category, metrics]) => (
              <div key={category}>
                <div style={s.sectionHeader}>
                  <span style={s.sectionTitle}>{category}</span>
                  <span style={s.sectionCount}>
                    {String(metrics.length)} metrics
                  </span>
                </div>
                <div
                  style={{ ...s.metricsRow, marginTop: "var(--sz-space-2)" }}
                >
                  {metrics.map((m) => (
                    <MetricCard key={m.id} metric={m} />
                  ))}
                </div>
              </div>
            ),
          )}
        </>
      ) : (
        <div style={s.emptyState}>
          <span style={s.emptyIcon}>◇</span>
          <span style={s.emptyTitle}>No Trust Metrics</span>
          <span style={s.emptyNote}>
            Transparency and trust metrics will be populated as the system
            builds its track record.
          </span>
        </div>
      )}

      {/* Knowledge Articles */}
      <div>
        <div style={s.sectionHeader}>
          <span style={s.sectionTitle}>Knowledge Base</span>
        </div>

        {selectedArticle ? (
          <ArticleDetail
            article={selectedArticle}
            onBack={() => setSelectedArticle(null)}
          />
        ) : (
          <>
            {/* Type tabs */}
            <div style={{ ...s.tabBar, marginTop: "var(--sz-space-2)" }}>
              {ARTICLE_TABS.map((tab) => {
                const count =
                  tab.key === "all"
                    ? articlesState.articles.length
                    : (typeCounts[tab.key] ?? 0);
                return (
                  <button
                    key={tab.key}
                    type="button"
                    style={
                      activeTab === tab.key ? s.tabItemActive : s.tabItem
                    }
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                    {count > 0 ? ` (${String(count)})` : ""}
                  </button>
                );
              })}
            </div>

            {/* Articles grid */}
            {articlesState.articles.length === 0 ? (
              <div
                style={{ ...s.emptyState, marginTop: "var(--sz-space-2)" }}
              >
                <span style={s.emptyIcon}>▤</span>
                <span style={s.emptyTitle}>No Articles</span>
                <span style={s.emptyNote}>
                  Methodology documentation, trust policies, and FAQ entries
                  will appear here as content is published.
                </span>
              </div>
            ) : (
              <div
                style={{
                  ...s.articlesGrid,
                  marginTop: "var(--sz-space-2)",
                }}
              >
                {articlesState.articles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    article={article}
                    onSelect={setSelectedArticle}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <span style={s.phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
